// Imports for external libraries go here.
import React, { FC, useState } from 'react';
import clsx from 'clsx';

// Imports for internal (to the monorepo) libraries go here,
// separated by a blank line from external imports.
import { Icon, Types, Text, InputTextField } from '@marriott/mi-ui-library';
// The closer the import is to the file the lower it should be in this list.
import { PasswordInputProps } from './PasswordInput.types';
import { StyledPasswordInput } from './PasswordInput.styles';

// Use named rather than default exports.
export const PasswordInput: FC<PasswordInputProps> = ({
  hasError,
  value,
  disable,
  label,
  name,
  id,
  passwordType,
  error,
  isValid,
  iconClass,
  customClass,
  labelClass,
  showValidations,
  passwordConditions,
  inputCustomClass,
  showCheckIcon,
  onIconClick,
  handleChange,
  ariaLabel,
  descriptionClass,
  errorLabelClass,
  innerLabel,
  checkHeaderLabel,
}) => {
  const [isActive, setIsActive] = useState(false);
  // Checks must be visible only when :
  // 1. input field is active even if user have not entered any value
  // 2. however if user enters any value checks must be visible and must not be hidden
  const showValidationChecks =
    showValidations && (!innerLabel || (innerLabel && ((value?.length && value?.length > 0) || isActive)));
  return (
    <StyledPasswordInput data-component-name="m-account-PasswordInput" data-testid="account-PasswordInput">
      <div
        className={clsx(
          innerLabel ? 'm-input-text-field' : 'm-input-field',
          disable ? 'is-disabled' : hasError ? 'is-error' : value ? 'is-active' : '',
          customClass
        )}
      >
        <InputTextField
          infoLabel={id}
          label={label}
          inputValue={value}
          inputMaxLength={20}
          className={inputCustomClass ?? ''}
          disabled={disable}
          getLabelProps={() => ({
            htmlFor: id,
            className: labelClass,
          })}
          getInputProps={() => ({
            autoComplete: 'off',
            id: id,
            name: name,
            type: passwordType,
            'aria-label': ariaLabel,
            'data-testId': id,
          })}
          getInputValue={(value: string) => handleChange?.(name || '', value)}
          customDownshiftLabel={true}
          setErrorHtml={!!innerLabel}
          messageToShow={error}
          showErrorMessage={hasError}
          messageClass={errorLabelClass || ''}
          testId="password-input"
          showIcon={!!innerLabel}
          iconClass={innerLabel ? clsx(`icon ${disable && 'fade-icon'}`, iconClass) : ''}
          iconOnClick={!disable && !!innerLabel ? onIconClick : undefined}
          iconAriaLabel={iconClass}
          onFocus={() => setIsActive(true)}
          onBlur={() => setIsActive(false)}
        />
        {!innerLabel && (
          <span
            className={clsx(`icon ${disable && 'fade-icon'}`, iconClass)}
            onClick={!disable ? onIconClick : undefined}
          ></span>
        )}
        {!innerLabel && showCheckIcon && (
          <Icon
            iconClass={clsx(
              'icon password-valid-check',
              innerLabel ? 'password-valid-check__inline' : 'password-valid-check__standard',
              isValid ? 'icon-check green-check' : value?.length ? 'icon-clear red-clear' : ''
            )}
          />
        )}
      </div>
      {showValidationChecks && (
        <div className={clsx('password-checks mb-5', disable && 'is-disabled')}>
          {innerLabel && (
            <Text
              copyText={checkHeaderLabel}
              fontSize={Types.size.small}
              element={Types.tags.span}
              customClass="t-font-alt-s"
            />
          )}
          <div className="list-items">
            {passwordConditions?.map(password => {
              return (
                <div className="d-flex align-items-baseline" key={password.condition}>
                  <Icon
                    iconClass={clsx(
                      value?.length
                        ? {
                            'icon-check green-check': password.isValid,
                            'icon-clear red-clear': !password.isValid && !innerLabel,
                            'icon-cancel red-clear': !password.isValid && innerLabel && !isActive,
                            'icon icon-dot': !password.isValid && innerLabel && isActive,
                          }
                        : `icon icon-dot ${innerLabel ? 'mr-2' : 'mr-1'}`,
                      innerLabel && 'mr-2'
                    )}
                  />
                  <Text
                    copyText={password.condition}
                    fontSize={innerLabel ? Types.size.small : Types.size.extraSmall}
                    element={Types.tags.span}
                    customClass={clsx(
                      descriptionClass ?? '',
                      innerLabel && value?.length && (password.isValid ? 't-font-alt-s' : !isActive && 'red-clear')
                    )}
                  />
                </div>
              );
            })}
          </div>
        </div>
      )}
    </StyledPasswordInput>
  );
};
